import React from "react";
import {
  AlignCenter,
  AlignCenterHorizontal,
  AlignEndHorizontal,
  AlignLeft,
  AlignRight,
  AlignStartHorizontal,
  ArrowDown,
  ArrowUp,
  Copy,
  Pencil,
  Plus,
  Settings2,
  Trash2,
} from "lucide-react";
import { Button } from "@cms/ui/components/button";
import type { ColumnAlign, PageSection, SectionColumn } from "../lib/editor-content";
import {
  columnAlignClasses,
  columnPreviewHtml,
  designTokenStyle,
  sectionPreviewShell,
  timelinePreviewHtml,
} from "../lib/section-preview";
import "./section-canvas.css";

/** A patch to one column's alignment; an undefined value clears the axis. */
export type ColumnAlignPatch = Partial<Pick<SectionColumn, "align" | "valign">>;

interface SectionCanvasProps {
  sections: PageSection[];
  /** The tenant's design_tokens JSON from /tenant/config (or null). */
  designTokens: string | null;
  /** The section whose properties sidebar is open (null when closed). */
  propertiesId: string | null;
  onEditColumn: (id: string, column: number) => void;
  onAlignColumn: (id: string, column: number, patch: ColumnAlignPatch) => void;
  onToggleProperties: (id: string) => void;
  onMove: (id: string, direction: -1 | 1) => void;
  onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
  onAdd: () => void;
}

const HORIZONTAL_ALIGNS: Array<{ value: ColumnAlign; label: string; Icon: typeof AlignLeft }> = [
  { value: "start", label: "Align content left", Icon: AlignLeft },
  { value: "center", label: "Align content centre", Icon: AlignCenter },
  { value: "end", label: "Align content right", Icon: AlignRight },
];

const VERTICAL_ALIGNS: Array<{ value: ColumnAlign; label: string; Icon: typeof AlignLeft }> = [
  { value: "start", label: "Align content top", Icon: AlignStartHorizontal },
  { value: "center", label: "Align content middle", Icon: AlignCenterHorizontal },
  { value: "end", label: "Align content bottom", Icon: AlignEndHorizontal },
];

/** One start/centre/end icon trio; clicking the active value clears the axis. */
function AlignGroup({ options, current, onSelect }: {
  options: typeof HORIZONTAL_ALIGNS;
  current: ColumnAlign | undefined;
  onSelect: (value: ColumnAlign | undefined) => void;
}) {
  return (
    <div className="flex overflow-hidden rounded-md border bg-background shadow">
      {options.map(({ value, label, Icon }) => (
        <button
          key={value}
          type="button"
          title={label}
          aria-label={label}
          aria-pressed={current === value}
          className={`flex h-7 w-7 items-center justify-center transition-colors ${
            current === value
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:bg-muted hover:text-foreground"
          }`}
          onClick={() => onSelect(current === value ? undefined : value)}
        >
          <Icon className="h-3.5 w-3.5" />
        </button>
      ))}
    </div>
  );
}

/**
 * One column of a section on the canvas: read-only preview HTML with its own
 * hover tint + outline. Clicking anywhere on the column opens the per-column
 * content modal (the centred "Edit content" chip is a visual affordance only);
 * the bottom-middle hover controls set the column's horizontal/vertical
 * alignment without opening the editor.
 */
function ColumnPreview({ column, multi, onEdit, onAlign }: {
  column: SectionColumn;
  multi: boolean;
  onEdit: () => void;
  onAlign: (patch: ColumnAlignPatch) => void;
}) {
  const html = columnPreviewHtml(column.blocks);
  const alignClasses = columnAlignClasses(column);
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label="Edit content"
      className={`group/col relative cursor-pointer ${multi ? "page-section__col" : ""}${alignClasses ? ` ${alignClasses}` : ""}`}
      onClick={onEdit}
      onKeyDown={(e: React.KeyboardEvent) => {
        // Only the column itself — Enter/Space on the alignment buttons must
        // not bubble into opening the editor.
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onEdit();
        }
      }}
    >
      {html ? (
        <div dangerouslySetInnerHTML={{ __html: html }} />
      ) : (
        <p className="pgs-site-empty py-6">Empty column</p>
      )}

      {/* Per-column hover tint + outline. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -inset-2 rounded bg-primary/5 opacity-0 outline outline-2 -outline-offset-2 outline-primary/60 transition-opacity group-focus-within/col:opacity-100 group-hover/col:opacity-100"
      />

      {/* Centred edit affordance (decorative — the whole column is the target). */}
      <div
        aria-hidden="true"
        className="pgs-ui pointer-events-none absolute inset-0 flex items-center justify-center opacity-0 transition-opacity group-focus-within/col:opacity-100 group-hover/col:opacity-100"
      >
        <Button size="sm" variant="secondary" className="pointer-events-none shadow" tabIndex={-1}>
          <Pencil className="h-4 w-4" />
          Edit content
        </Button>
      </div>

      {/* Alignment controls (bottom middle) — clicks stay inside the controls. */}
      <div
        className="pgs-ui absolute -bottom-1 left-1/2 z-10 flex -translate-x-1/2 gap-1.5 opacity-0 transition-opacity group-focus-within/col:opacity-100 group-hover/col:opacity-100"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
      >
        <AlignGroup
          options={HORIZONTAL_ALIGNS}
          current={column.align}
          onSelect={(align) => onAlign({ align })}
        />
        <AlignGroup
          options={VERTICAL_ALIGNS}
          current={column.valign}
          onSelect={(valign) => onAlign({ valign })}
        />
      </div>
    </div>
  );
}

/**
 * A timeline section's canvas preview: the whole timeline is one hover/click
 * target opening the timeline editor (steps have no per-column alignment).
 */
function TimelinePreview({ section, onEdit }: {
  section: PageSection;
  onEdit: () => void;
}) {
  const html = section.timeline ? timelinePreviewHtml(section.timeline) : "";
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label="Edit timeline"
      className="group/col relative cursor-pointer"
      onClick={onEdit}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onEdit();
        }
      }}
    >
      {html ? (
        <div dangerouslySetInnerHTML={{ __html: html }} />
      ) : (
        <p className="pgs-site-empty py-6">Empty timeline — add your first step</p>
      )}

      {/* Hover tint + outline (as on columns). */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -inset-2 rounded bg-primary/5 opacity-0 outline outline-2 -outline-offset-2 outline-primary/60 transition-opacity group-focus-within/col:opacity-100 group-hover/col:opacity-100"
      />

      {/* Centred edit affordance (decorative — the whole timeline is the target). */}
      <div
        aria-hidden="true"
        className="pgs-ui pointer-events-none absolute inset-0 flex items-center justify-center opacity-0 transition-opacity group-focus-within/col:opacity-100 group-hover/col:opacity-100"
      >
        <Button size="sm" variant="secondary" className="pointer-events-none shadow" tabIndex={-1}>
          <Pencil className="h-4 w-4" />
          Edit timeline
        </Button>
      </div>
    </div>
  );
}

/**
 * The Content tab's editing canvas: every section rendered full-width in the
 * site's look (client design tokens applied). Each column is its own hover
 * target opening the single-column content modal; section-level properties
 * (columns, background, width, padding, …) live in the Properties overlay.
 */
export function SectionCanvas({
  sections,
  designTokens,
  propertiesId,
  onEditColumn,
  onAlignColumn,
  onToggleProperties,
  onMove,
  onDuplicate,
  onDelete,
  onAdd,
}: SectionCanvasProps) {
  return (
    <div>
      <div className="pgs-site" style={designTokenStyle(designTokens)}>
        {sections.map((section, index) => {
          const shell = sectionPreviewShell(section);
          const multi = section.settings.columns > 1;
          return (
            <div key={section.id} className="group relative">
              <section
                className={`${shell.className}${shell.imagePending ? " page-section--bg-pending" : ""}`}
                style={shell.style}
              >
                <div className={shell.innerClassName}>
                  {section.type === "timeline" ? (
                    <TimelinePreview
                      section={section}
                      onEdit={() => onEditColumn(section.id, 0)}
                    />
                  ) : (
                    section.columns.slice(0, section.settings.columns).map((col, colIndex) => (
                      <ColumnPreview
                        key={colIndex}
                        column={col}
                        multi={multi}
                        onEdit={() => onEditColumn(section.id, colIndex)}
                        onAlign={(patch) => onAlignColumn(section.id, colIndex, patch)}
                      />
                    ))
                  )}
                </div>
              </section>

              {/* Section controls (visible on hover / keyboard focus). */}
              <div className="pgs-ui absolute right-3 top-3 flex gap-1 opacity-0 transition-opacity focus-within:opacity-100 group-focus-within:opacity-100 group-hover:opacity-100">
                <Button
                  size="sm"
                  variant="secondary"
                  className="shadow"
                  aria-expanded={propertiesId === section.id}
                  onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
                  onClick={() => onToggleProperties(section.id)}
                >
                  <Settings2 className="h-4 w-4" />
                  Properties
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  className="shadow"
                  disabled={index === 0}
                  onClick={() => onMove(section.id, -1)}
                  aria-label="Move section up"
                >
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  className="shadow"
                  disabled={index === sections.length - 1}
                  onClick={() => onMove(section.id, 1)}
                  aria-label="Move section down"
                >
                  <ArrowDown className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  className="shadow"
                  onClick={() => onDuplicate(section.id)}
                  aria-label="Duplicate section"
                >
                  <Copy className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  className="shadow text-destructive hover:text-destructive"
                  onClick={() => onDelete(section.id)}
                  aria-label="Delete section"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          );
        })}

        {sections.length === 0 && (
          <p className="pgs-ui px-6 py-16 text-center text-sm text-muted-foreground">
            This page has no content yet. Add your first section to get started.
          </p>
        )}
      </div>

      <div className="border-t p-4">
        <Button type="button" variant="outline" className="w-full border-dashed" onClick={onAdd}>
          <Plus className="h-4 w-4" />
          Add section
        </Button>
      </div>
    </div>
  );
}
